import React from "react";

import { css } from "@compiled/react";
import {
    UniformSlot,
    UniformSlotWrapperComponentProps
} from "@uniformdev/canvas-react";

import { media } from "@atlaskit/primitives";

import {
    WCL_GRID_MAX_WIDTH,
    WCL_GRID_PADDING_LG
} from "@/libraries/design/foundations/grid/src/base";
import {
    SECTION_DIVIDER_HEIGHTS,
    SectionDividerType
} from "@/libraries/design/utils";

export type UniformSplitColorBackgroundContainerProps = {
    topColor: string;
    bottomColor: string;
    sectionDivider?: SectionDividerType;
    extendBackgroundColor?: boolean;
};

const UniformSplitColorBackgroundContainer: React.FC<
    UniformSplitColorBackgroundContainerProps
    // eslint-disable-next-line complexity
> = ({ topColor, bottomColor, sectionDivider, extendBackgroundColor }) => {
    const baseStyle = css({
        position: "relative"
    });

    const horizontalStyle = css({
        background: `linear-gradient(to bottom, ${topColor} 0%, ${topColor} 50%, ${bottomColor} 50%, ${bottomColor} 100%)`
    });

    const highLeft = css({
        "::before": {
            clipPath: "polygon(0% 0%, 0% 100%, 100% 100%)"
        }
    });
    const highRight = css({
        "::before": {
            clipPath: "polygon(100% 0%, 0% 100%, 100% 100%)"
        }
    });
    const coverStyles = css({
        "::before": {
            content: "",
            display: "block",
            width: "100%",
            position: "absolute",
            insetBlockStart: 0,
            backgroundColor: `${topColor}`,
            height: `calc(((100% - ${SECTION_DIVIDER_HEIGHTS.mobile}px) / 2) + 1px)`,
            [media.above.sm]: {
                height: `calc(((100% - ${SECTION_DIVIDER_HEIGHTS.tablet}px) / 2) + 1px)`
            },
            [media.above.md]: {
                height: `calc(((100% - ${SECTION_DIVIDER_HEIGHTS.desktop}px) / 2) + 1px)`
            }
        },
        "::after": {
            content: "",
            display: "block",
            width: "100%",
            position: "absolute",
            zIndex: -1,
            insetBlockEnd: 0,
            backgroundColor: `${bottomColor}`,
            height: `calc(((100% - ${SECTION_DIVIDER_HEIGHTS.mobile}px) / 2) + 1px)`,
            [media.above.sm]: {
                height: `calc(((100% - ${SECTION_DIVIDER_HEIGHTS.tablet}px) / 2) + 1px)`
            },
            [media.above.md]: {
                height: `calc(((100% - ${SECTION_DIVIDER_HEIGHTS.desktop}px) / 2) + 1px)`
            }
        }
    });
    const sectionDividerComponent = (
        <div
            css={[
                css({
                    "position": "absolute",
                    "height": `calc(${SECTION_DIVIDER_HEIGHTS.mobile}px + 2px)`,
                    "width": "100%",
                    "background": `${topColor}`,
                    "insetBlockStart": "50%",
                    "transform": "translateY(-50%)",
                    [media.above.sm]: {
                        height: `calc(${SECTION_DIVIDER_HEIGHTS.tablet}px + 2px)`
                    },
                    [media.above.md]: {
                        height: `calc(${SECTION_DIVIDER_HEIGHTS.desktop}px + 2px)`
                    },
                    "::before": {
                        content: "",
                        display: "block",
                        height: "100%",
                        width: "100%",
                        position: "absolute",
                        insetBlockStart: 0,
                        backgroundColor: `${bottomColor}`
                    }
                }),
                bottomColor === "transparent" &&
                    sectionDivider === "high-left" &&
                    css({ clipPath: "polygon(0% 0%, 100% 0%, 100% 100%)" }),
                bottomColor === "transparent" &&
                    sectionDivider === "high-right" &&
                    css({ clipPath: "polygon(0% 0%,  0% 100%, 100% 0%)" }),
                sectionDivider === "high-left" && highLeft,
                sectionDivider === "high-right" && highRight
            ]}
        />
    );

    const extendedBackgroundColorComponent = (
        <div
            css={[
                css({
                    position: "absolute",
                    width: "100vw",
                    height: "100%",
                    [media.only.sm]: {
                        width: `calc(100vw - (2 * ${WCL_GRID_PADDING_LG}px))`
                    },
                    [media.above.sm]: {
                        width: `calc(${WCL_GRID_MAX_WIDTH}px + ((100vw - ${WCL_GRID_MAX_WIDTH}px) / 2))`, // for when the grid width is stopped at its max with
                        maxWidth: `min(calc(100% - ${WCL_GRID_PADDING_LG}px), calc(${WCL_GRID_MAX_WIDTH}px + 80px))` // for when the grid width is less than its max width, and to have background color max width at grid max width + 80px
                    },
                    insetInlineStart: "50%",
                    transform: "translateX(-50%)"
                }),
                sectionDivider && coverStyles,
                !sectionDivider && horizontalStyle
            ]}
        >
            {sectionDivider && sectionDividerComponent}
        </div>
    );

    const slotWrapper = ({ items }: UniformSlotWrapperComponentProps) => {
        return <div css={css({ position: "relative" })}>{items}</div>;
    };

    return (
        <div
            css={[
                baseStyle,
                !extendBackgroundColor && sectionDivider && coverStyles,
                !extendBackgroundColor && !sectionDivider && horizontalStyle
            ]}
        >
            {sectionDivider &&
                !extendBackgroundColor &&
                sectionDividerComponent}
            {extendBackgroundColor && extendedBackgroundColorComponent}
            <UniformSlot
                name="component"
                wrapperComponent={sectionDivider ? slotWrapper : undefined}
            />
        </div>
    );
};

export default UniformSplitColorBackgroundContainer;
